import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import DOMPurify from "dompurify";
import { JOURNAL_ENTRIES, DARRYL_PHOTO } from "@/lib/content";
import SEO from "@/components/SEO";
import { DashTitle } from "@/components/DashTitle";
import RegulatoryAdvisorySignup from "@/components/RegulatoryAdvisorySignup";
import { articleSchema, breadcrumbSchema } from "@/lib/schemas";
import { SITE } from "@/lib/seo";

// -----------------------------------------------------------------------------
// Safe HTML rendering for essay body inlines.
//
// The essay body content lives in src/lib/content.js — first-party editorial
// text authored by the firm. It uses a small subset of inline HTML for
// emphasis (<em>, <strong>) and firm-controlled anchor tags (<a href="/…">).
//
// Even though the source is trusted, we run every string through DOMPurify
// before rendering so that any future authoring mistake, imported feed, or
// accidental HTML paste cannot become an XSS vector.
// -----------------------------------------------------------------------------
const HTML_ALLOWED_TAGS = ["em", "strong", "b", "i", "a", "br", "span"];
const HTML_ALLOWED_ATTR = ["href", "title", "rel", "target", "class"];

function sanitize(html = "") {
  if (typeof html !== "string") return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: HTML_ALLOWED_TAGS,
    ALLOWED_ATTR: HTML_ALLOWED_ATTR,
    // Force any anchor with a target attribute to also carry rel=noopener
    // noreferrer so the origin cannot be tampered with by a linked page.
    ADD_ATTR: ["target", "rel"],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    RETURN_TRUSTED_TYPE: false,
  });
}
const safeHtml = (html) => ({ __html: sanitize(html) });

// --- Extra essay metadata — retained here for future publication only.
// They have no body and no live route yet, so they are deliberately
// EXCLUDED from ALL_ESSAYS so /journal/:slug and prev/next navigation
// never surface them as clickable public links.
const EXTRA_ESSAYS = [];

const ALL_ESSAYS = [...JOURNAL_ENTRIES];

// --- Renders a single body block from the article's `body` array ---------
function Block({ b }) {
  if (b.type === "lede")
    return (
      <p
        className="text-2xl md:text-[1.6rem] font-cormorant italic text-navy leading-[1.4] mb-8"
        dangerouslySetInnerHTML={safeHtml(b.text)}
      />
    );
  if (b.type === "p")
    return (
      <p
        className="text-lg md:text-xl text-slate2 font-light leading-[1.75] mb-6"
        dangerouslySetInnerHTML={safeHtml(b.text)}
      />
    );
  if (b.type === "h2")
    return (
      <h2 className="mt-16 mb-6 font-display text-navy text-3xl md:text-[2.25rem] leading-[1.15]">
        {b.text}
      </h2>
    );
  if (b.type === "h3")
    return (
      <h3 className="mt-10 mb-4 font-display text-navy text-2xl md:text-[1.65rem] leading-[1.2]">
        {b.text}
      </h3>
    );
  if (b.type === "contactcard")
    return (
      <aside className="my-14 bg-navy text-ivory text-center py-12 px-6 md:px-10" data-testid="article-contact-card">
        <div className="font-display text-2xl md:text-3xl">Lone Star &#9733; Counsel</div>
        <div className="mt-3 text-[11px] uppercase tracking-[0.3em] text-brass">Attorneys | Counselors | Business Advisors</div>
        <div className="mt-2 text-sm text-ivory/75 font-serif">A d/b/a of Pratt Law Group, PLLC</div>
        <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xl md:text-2xl font-serif">
          {b.phones.map((p, i) => (
            <a key={p.tel} href={`tel:${p.tel}`} className="text-ivory hover:text-brass underline-offset-4 hover:underline" data-testid={`article-phone-${i}`}>{p.label}</a>
          ))}
        </div>
        <a href="https://lonestarcounsel.com" className="mt-2 inline-block text-ivory/85 font-serif hover:text-brass">LoneStarCounsel.com</a>
        <div className="mt-5 text-[11px] uppercase tracking-[0.32em] text-brass">Planning Today. Protecting Tomorrow.&reg;</div>
        <Link to={b.href} className="btn-gold inline-flex mt-8" data-testid="article-request-consultation">{b.cta}</Link>
      </aside>
    );
  if (b.type === "pullquote")
    return (
      <blockquote className="my-14 border-l-2 border-brass pl-8 py-2">
        <p
          className="font-cormorant italic text-2xl md:text-3xl text-navy leading-[1.35]"
          dangerouslySetInnerHTML={safeHtml(b.text)}
        />
      </blockquote>
    );
  if (b.type === "list")
    return (
      <ul className="mb-8 space-y-4 list-none">
        {b.items.map((it, i) => (
          <li key={i} className="flex gap-4 text-lg md:text-xl text-slate2 font-light leading-[1.7]">
            <span className="text-brass font-display text-xl leading-[1.4] shrink-0">·</span>
            <span className="min-w-0 [overflow-wrap:anywhere]" dangerouslySetInnerHTML={safeHtml(it)} />
          </li>
        ))}
      </ul>
    );
  if (b.type === "signoff")
    return (
      <p
        className="mt-16 pt-8 border-t-0 text-sm text-slate2/80 italic font-light leading-relaxed"
        dangerouslySetInnerHTML={safeHtml(b.text)}
      />
    );
  if (b.type === "callout")
    return (
      <aside
        className="my-14 bg-linen border-y border-transparent py-8 px-6 md:px-10 -mx-2 md:mx-0"
        data-testid="article-callout"
      >
        <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium mb-3">
          {b.kicker || "The Firm"}
        </div>
        <div
          className="font-display text-navy text-xl md:text-2xl leading-[1.3] mb-5"
          dangerouslySetInnerHTML={safeHtml(b.title)}
        />
        {b.href && b.cta && (
          <Link to={b.href} className="btn-gold inline-flex" data-testid="article-callout-cta">
            {b.cta}
          </Link>
        )}
      </aside>
    );
  if (b.type === "sharepanel")
    return (
      <aside
        className="my-14 border-t border-b border-brass/25 py-10 px-6 md:px-10 -mx-2 md:mx-0"
        data-testid="article-share-panel"
      >
        <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium mb-3">
          {b.kicker || "Share this article"}
        </div>
        {b.title && (
          <div
            className="font-display text-navy text-xl md:text-2xl leading-[1.3] mb-6"
            dangerouslySetInnerHTML={safeHtml(b.title)}
          />
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(b.urls || []).map((u, i) => (
            <a
              key={i}
              href={u.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block border border-navy/15 hover:border-oxblood hover:bg-ivory transition-colors px-5 py-4"
              data-testid={`article-share-${u.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                {u.label}
              </div>
              <div className="mt-2 font-serif text-sm text-navy break-all group-hover:text-oxblood">
                {u.url}
              </div>
            </a>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate2/70 font-serif leading-relaxed">
          Each link points to this article with a distinct source tag. Post the Facebook link on Facebook, the LinkedIn link on LinkedIn, and the Google Business Profile link on Google Business Profile so inquiries can be attributed to the channel that brought them.
        </p>
      </aside>
    );
  return null;
}

export default function JournalArticle() {
  const { slug } = useParams();
  const article = useMemo(() => ALL_ESSAYS.find((a) => a.slug === slug), [slug]);

  if (!article) return <Navigate to="/journal" replace />;

  const idx = ALL_ESSAYS.findIndex((a) => a.slug === slug);
  const prev = idx > 0 ? ALL_ESSAYS[idx - 1] : null;
  const next = idx < ALL_ESSAYS.length - 1 ? ALL_ESSAYS[idx + 1] : null;
  const hasBody = Array.isArray(article.body) && article.body.length > 0;

  return (
    <div data-testid="journal-article-page">
      <SEO
        title={article.seoTitle || article.title}
        description={article.metaDescription || article.excerpt}
        path={`/journal/${article.slug}`}
        ogImage={`${SITE.url}${article.ogImage || article.cover}`}
        ogImageWidth={article.coverSquare ? 1254 : undefined}
        ogImageHeight={article.coverSquare ? 1254 : undefined}
        ogImageAlt={article.coverAlt}
        ogType="article"
        schema={[
          articleSchema(article),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Journal", path: "/journal" },
            { name: article.title, path: `/journal/${article.slug}` },
          ]),
        ]}
      />

      {/* Article HERO — editorial cover + kicker + title + byline */}
      <section className="bg-linen border-b-0" data-testid="article-hero">
        {/* Opening watermark — signature seal above the article */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-2 flex flex-col items-center text-center" data-testid="article-watermark">
          <div className="flex items-center gap-4">
            <span className="h-px w-12 bg-brass/50" />
            <svg width="14" height="14" viewBox="0 0 100 100" aria-hidden="true" style={{ filter: "drop-shadow(0 1px 0 rgba(157,126,76,0.25))" }}>
              <polygon
                points="50,4 60.98,36.18 95,37.64 66.51,56.72 78.09,89.36 50,68.5 21.91,89.36 33.49,56.72 5,37.64 39.02,36.18"
                fill="#9D7E4C"
                stroke="#7A6338"
                strokeWidth="1"
              />
            </svg>
            <span className="h-px w-12 bg-brass/50" />
          </div>
          <div className="mt-4 text-[9px] uppercase tracking-[0.42em] text-brass font-medium">
            Lone Star Counsel
          </div>
          <div className="mt-1 font-cormorant italic text-slate2 text-sm">
            An essay from the Journal
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Cover */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            {article.coverSquare ? (
              <div className="aspect-square w-full max-w-[640px] mx-auto overflow-hidden bg-[#031E3D]">
                <img
                  src={article.cover}
                  srcSet={`${article.coverSmall} 720w, ${article.cover} 1254w`}
                  sizes="(min-width: 1024px) 640px, 100vw"
                  width="1254"
                  height="1254"
                  alt={article.coverAlt || article.title}
                  className="w-full h-full object-contain"
                  loading="eager"
                  fetchpriority="high"
                  data-testid="article-cover-image"
                />
              </div>
            ) : (
            <div className="aspect-[4/3] overflow-hidden bg-parchment">
              <img
                src={article.cover}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            )}
          </div>
          {/* Title */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              <span className="h-px w-8 bg-brass/60" />
              {article.category}
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.32em] text-slate2/60">
              {article.no}
            </div>
            <h1 className="mt-6 font-display text-navy text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05]">
              <DashTitle text={article.title} />
            </h1>
            <p
              className="mt-6 text-lg md:text-xl font-cormorant italic text-slate2 leading-[1.4]"
              data-testid="article-excerpt"
            >
              {article.subtitle || article.excerpt}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <img
                src={DARRYL_PHOTO}
                alt={article.author}
                className="w-12 h-12 rounded-full object-cover object-top grayscale-[10%]"
                loading="eager"
              />
              <div className="leading-tight">
                <div className="text-[12px] uppercase tracking-[0.22em] text-navy font-semibold">
                  {article.author}
                </div>
                <div className="text-[10px] uppercase tracking-[0.26em] text-brass mt-0.5">
                  Managing Partner
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.20em] text-slate2/70">
                  {article.date} <span className="text-brass/60 mx-1.5">·</span> {article.minutes} min read
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY — long-form editorial */}
      <section className="bg-ivory py-20 md:py-28" data-testid="article-body">
        <div className="max-w-[820px] mx-auto px-6 md:px-8">
          {hasBody ? (
            article.body.map((b, i) => <Block key={i} b={b} />)
          ) : (
            <div className="text-center py-20">
              <div className="inline-flex items-center gap-3">
                <span className="h-px w-10 bg-brass/60" />
                <Star size={11} className="text-brass fill-brass" />
                <span className="h-px w-10 bg-brass/60" />
              </div>
              <h2 className="mt-6 font-display text-navy text-3xl leading-tight">
                This essay is in the press.
              </h2>
              <p className="mt-4 text-lg text-slate2 font-light max-w-xl mx-auto italic font-cormorant">
                The complete essay will appear in this volume shortly. In the interim,
                the excerpt above conveys the argument.
              </p>
              <div className="mt-10">
                <Link to="/intake" className="btn-gold" data-testid="article-placeholder-cta">
                  Discuss this Topic with the Firm
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* PREV / NEXT navigation */}
      <section className="bg-linen border-t-0 py-14" data-testid="article-nav">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          {prev ? (
            <Link
              to={`/journal/${prev.slug}`}
              className="group flex items-start gap-4 hover:text-oxblood transition-colors"
              data-testid="article-prev"
            >
              <ArrowLeft size={18} className="mt-1 text-brass shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/70 mb-1">Previous Essay</div>
                <div className="font-display text-xl md:text-2xl text-navy group-hover:text-oxblood leading-tight">
                  {prev.title}
                </div>
              </div>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to={`/journal/${next.slug}`}
              className="group flex items-start gap-4 md:justify-end md:text-right hover:text-oxblood transition-colors"
              data-testid="article-next"
            >
              <div className="md:order-1">
                <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/70 mb-1">Next Essay</div>
                <div className="font-display text-xl md:text-2xl text-navy group-hover:text-oxblood leading-tight">
                  {next.title}
                </div>
              </div>
              <ArrowRight size={18} className="mt-1 text-brass shrink-0 md:order-2" />
            </Link>
          ) : (
            <span />
          )}
        </div>
      </section>

      {/* Regulatory advisories signup — appears on Business & Regulatory
          essays as a warm opt-in for future federal/state advisories. */}
      {article?.category === "Business & Regulatory" && (
        <section className="bg-ivory py-16 md:py-20" data-testid="article-advisory-signup">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <RegulatoryAdvisorySignup variant="card" />
          </div>
        </section>
      )}

      {/* Closer — return to journal + firm CTA (light modern panel) */}
      <section className="bg-linen py-20 md:py-24" data-testid="article-closer">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-3">
            <span className="h-px w-10 bg-brass/60" />
            <Star size={11} className="text-brass fill-brass" />
            <span className="h-px w-10 bg-brass/60" />
          </div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-4xl leading-tight">
            Considering counsel on this topic?
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-slate2 font-cormorant italic text-lg md:text-xl">
            Every engagement begins with a private conference. The first hour is on the firm.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/intake" className="btn-gold" data-testid="article-cta-intake">
              Request a Conference
            </Link>
            <Link
              to="/journal"
              className="btn-ghost"
              data-testid="article-cta-journal"
            >
              Return to the Journal
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
